<template>
    <div :class="outerWrapperClass">
        <slot
            name="template"
            :onImageDeleteClick="onImageDeleteClick"
            :onDeleteClick="onDeleteClick"
            :onOptionTextChange="onOptionTextChange"
            :onNotePlaceholderChange="onNotePlaceholderChange"
            :save="save"
            :saving="status.saving"
            :editing="editing"
            :text="contentUnit.text"
            :extras="extras"
            :thumbnailUrl="thumbnailUrl"
            :imageUrl="imageUrl">
        </slot>
        <b-modal
            ref="cropper-modal"
            @ok="onCropDone"
            @cancel="onCropCancel"
        >
            <vueCropper
                v-if="imageDataForCropping"
                :img="imageDataForCropping"
                ref="cropper"
                :title="$t('message.crop_the_image')"
                :style="{width: '100%', aspectRatio: 1}"
                :autoCrop="true"
                :autoCropWidth="cropLength"
                :autoCropHeight="cropLength"
                :can-move-box="false"
                :center-box="true"
                :can-move="true"
                :fixed="true"
                :info="false"
                :enlarge="2"
                mode="auto"
                :fixedNumber="[cropAspect, 1]"
                @imgLoad="onImgLoad"
            />
            <input type="range" min="0.2" max="1.5" step="0.02" v-model="scale" class="d-block my-4 w-100" />
        </b-modal>
    </div>
</template>

<script>
    import api from '../mixins/api';
    import promptConfirm from '../mixins/prompt-confirm';
    import loadImage from 'blueimp-load-image';
    import { VueCropper } from 'vue-cropper';

    export default {
        mixins: [api, promptConfirm],

        components: {
            vueCropper: VueCropper
        },

        props: {
            context: {
                required: true,
                type: Object
            },
            contentUnit: {
                required: true,
                type: Object
            },
            providerId: {
                required: true,
                type: Number
            },
            providerQuestionId: {
                required: true,
                type: Number
            },
            optionId: {
                required: true
            },
            editing: Boolean,
            outerWrapperClass: String,
            enableCrop: Boolean,
            // values like: 1/1, 16/9, 4/3 or null
            cropAspect: {
                default: 1,
                type: Number
            },
            imageMaxLength: {
                default: 1280,
                type: Number
            },
            onDelete: Function,
        },

        data: function() {
            var extras = {};
            if (typeof this.contentUnit.extras === 'string') {
                extras = JSON.parse(this.contentUnit.extras);
            } else if (this.contentUnit.extras) {
                extras = this.contentUnit.extras;
            }
            return {
                status: {
                    saving: false,
                },
                imageAttributes: {
                    width: this.contentUnit ? this.contentUnit.image_width : 0,
                    height: this.contentUnit ? this.contentUnit.image_height : 0
                },
                imageData: null,
                imageDataForCropping: null,
                extras: extras,
                scale: 1,
                cropLength: 320
            }
        },

        computed: {
            imageUrl: function() {
                return this.contentUnit.file_name ? this.context.storageEndpoint + this.contentUnit.file_name : null;
            },
            thumbnailUrl: function() {
                if (this.contentUnit.thumbnail_file_name) {
                    return this.context.storageEndpoint + this.contentUnit.thumbnail_file_name;
                }
                return null;
            }
        },

        watch: {
            'contentUnit.fileObject': function(newValue, oldValue) {
                if (newValue) {
                    this.loadImageAndPromptCrop();
                }
            },
            scale: function(newValue, oldValue) {
                this.$refs.cropper.scale = newValue;
            }
        },

        mounted() {
            if (this.contentUnit.fileObject) {
                this.loadImageAndPromptCrop();
            }
        },

        methods: {
            onImgLoad(isSuccess) {
                if (!isSuccess) {
                    return;
                }

                const $cropper = this.$refs.cropper;
                const cropperLength = $cropper.$data.w;
                const imageWidth = $cropper.trueWidth;
                const imageHeight = $cropper.trueHeight;
                if (imageHeight > imageWidth) {
                    this.scale = cropperLength / imageWidth;
                } else {
                    this.scale = cropperLength / imageHeight;
                }
            },
            loadImageAndPromptCrop() {
                this.loadImageWithOrientationFixed(this.contentUnit.fileObject, (canvas) => {
                    this.imageDataForCropping = canvas.toDataURL(this.contentUnit.fileObject.type, 1);
                    this.cropOutputType = this.contentUnit.fileObject.type.replace('image/', '');
                    this.$refs['cropper-modal'].show();
                    this.$nextTick(() => {
                        this.$refs.cropper.startCrop();
                    });
                });
            },
            save() {
                this.context.bus.$emit('changeStatusToSaving', this.providerQuestionId);
                this.status.saving = true;
                if (Vue.dingIsLocalId(this.contentUnit.id)) {
                    this.createOptionContentUnit(this.providerId, this.providerQuestionId, this.getFormData(), this.onContentUnitSaved);
                } else {
                    this.updateOptionContentUnit(this.providerId, this.contentUnit.id, this.getFormData(), this.onContentUnitSaved);
                }
            },

            getFormData() {
                var data = new FormData();
                if (this.imageData) {
                    data.append('image_width', this.imageAttributes.width);
                    data.append('image_height', this.imageAttributes.height);
                    data.append('image', this.dataURItoBlob(this.imageData));
                }
                data.append('text', this.contentUnit.text);
                data.append('extras', JSON.stringify(this.extras));
                return data;
            },

            onImageDeleteClick() {
                this.promptConfirm(this.$t('message.are_you_sure_to_delete_image'), this.$t('general.delete'), () => {
                    if (Vue.dingIsLocalId(this.contentUnit.id)) {
                        this.onContentUnitImageDeleted();
                    } else {
                        this.deleteContentUnitImage(this.contentUnit.id, (response, error) => {
                            if (error) {
                                this.promptAlert(error);
                            } else {
                                this.$emit('contentUnitSaved', this.optionId, {
                                    id: this.optionId,
                                    content_unit_id: response.data.content_unit.id,
                                    provider_question_id: this.providerQuestionId,
                                    content_unit: {
                                        id: response.data.content_unit.id,
                                        provider_id: this.providerId,
                                        text: response.data.content_unit.text,
                                        file_name: response.data.content_unit.file_name,
                                        image_width: response.data.content_unit.image_width,
                                        image_height: response.data.content_unit.image_height,
                                        thumbnail_file_name: response.data.content_unit.thumbnail_file_name,
                                        thumbnail_width: response.data.content_unit.thumbnail_width,
                                        thumbnail_height: response.data.content_unit.thumbnail_height,
                                        extras: response.data.content_unit.extras
                                    }
                                });
                                // reset fileObject to reduce loading when user opening this album
                            }
                        });
                    }
                });

            },

            onContentUnitSaved(response, error) {
                this.context.bus.$emit('changeStatusToNotSaving', this.providerQuestionId);
                if (error) {
                    this.promptAlert(error);
                } else {
                    // get real option id that was created by api (create) or that is already existing (update)
                    var realOptionId = response.data.option==null? this.optionId : response.data.option.id;

                    // emit the real option with option.content_unit to parent component
                    this.$emit('contentUnitSaved', this.optionId, {
                        id: realOptionId,
                        content_unit_id: response.data.content_unit.id,
                        provider_question_id: this.providerQuestionId,
                        content_unit: {
                            id: response.data.content_unit.id,
                            provider_id: this.providerId,
                            text: response.data.content_unit.text,
                            file_name: response.data.content_unit.file_name,
                            image_width: response.data.content_unit.image_width,
                            image_height: response.data.content_unit.image_height,
                            thumbnail_file_name: response.data.content_unit.thumbnail_file_name,
                            thumbnail_width: response.data.content_unit.thumbnail_width,
                            thumbnail_height: response.data.content_unit.thumbnail_height,
                            extras: response.data.content_unit.extras
                        }
                    });
                    // reset fileObject to reduce loading when user opening this album
                    this.contentUnit.fileObject = null;
                    this.imageData = null;
                }
                this.status.saving = false;
            },

            onContentUnitDeleted() {
                this.$emit('contentUnitDeleted', this.optionId, this.contentUnit.id);
                // this.contentUnit.file_name = response.data.content_unit.file_name;

                // reset fileObject to reduce loading when user opening this album
                this.contentUnit.fileObject = null;
                this.status.saving = false;
            },

            onDeleteClick() {
                this.promptConfirm(this.$t('message.are_you_sure_to_delete'), this.$t('general.delete'), () => {
                    if (Vue.dingIsLocalId(this.contentUnit.id)) {
                        this.onContentUnitDeleted();
                    } else {
                        this.deleteOptionContentUnit(this.providerId, this.contentUnit.id, (response, error) => {
                            if (error) {
                                this.promptAlert(error);
                            } else {
                                this.onContentUnitDeleted();
                            }
                        });
                    }
                });
            },

            onOptionTextChange(newText) {
                if (this.contentUnit.text === newText) {
                    return;
                }
                
                this.contentUnit.text = newText;
                this.save();
            },
            onNotePlaceholderChange(newNotePlaceholder) {
                this.extras.note_placeholder = newNotePlaceholder;
                this.save();
            },

            dataURItoBlob(dataURI) {
                var byteString;
                if (dataURI.split(',')[0].indexOf('base64') >= 0) {
                    byteString = atob(dataURI.split(',')[1]);
                } else {
                    byteString = unescape(dataURI.split(',')[1]);
                }

                // separate out the mime component
                var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

                // write the bytes of the string to a typed array
                var ia = new Uint8Array(byteString.length);
                for (var i = 0; i < byteString.length; i++) {
                    ia[i] = byteString.charCodeAt(i);
                }

                return new Blob([ia], { type:mimeString });
            },

            loadImageWithOrientationFixed(fileObject, cb) {
                loadImage(this.contentUnit.fileObject, (canvas) => {
                    this.imageAttributes.width = canvas.width;
                    this.imageAttributes.height = canvas.height;
                    cb(canvas);
                }, {
                    canvas: true,
                    orientation: true,
                    imageSmoothingQuality: 'high'
                });
            },

            onCropDone() {
                this.$refs.cropper.getCropData((data) => {
                    this.contentUnit.fileObject = null;
                    this.imageDataForCropping = null;
                    this.imageData = data;
                    this.$refs.cropper.stopCrop();
                    this.$refs['cropper-modal'].hide();

                    var i = new Image();
                    i.onload = () => {
                        this.imageAttributes.width = i.width;
                        this.imageAttributes.height = i.height;
                        this.save();
                    };
                    i.src = data;
                });
            },

            onCropCancel() {
                // Discard the selected file object
                this.contentUnit.fileObject = null;
                this.imageDataForCropping = null;
                this.$refs.cropper.stopCrop();
                this.$refs['cropper-modal'].hide();
            }
        }
    }
</script>

<style>
</style>
